import { memo } from 'react'
import { pdf } from '@react-pdf/renderer'

import { Box } from '@material-ui/core'

import { ReactComponent as Icon } from 'assets/icons/download-icon.svg'

import { SpacedGroup, WhiteButton } from 'UI/Components'
import { useBoolean, UseBooleanType, useBrandKit, useFavicon, useNotification } from 'hooks'

import { isQG, logToAnalytics, request } from 'modules'
import { PlaybookType } from 'app/types'

import { PdfDocument } from './PdfDocument'
import { PdfSlidesDocument } from './PdfSlidesDocument'

type Props = {
    playbookId: string
    fileName: string
    playbook: PlaybookType
    mode: PlaybookType['mode']
    disableMP4?: boolean
    disableGIF?: boolean
}

export const Download = memo(
    ({ playbook, disableGIF, disableMP4, playbookId, fileName, mode }: Props) => {
        const loadingMP4 = useBoolean()
        const loadingGif = useBoolean()

        const pdfLoading = useBoolean()
        const pdfSlidesLoading = useBoolean()

        const { brandKitHighlightColor, brandKitLoading } = useBrandKit()

        const favIcon = useFavicon(playbook.applications)

        const downloadPdfFile = (blob: Blob) => {
            const fileURL = window.URL.createObjectURL(blob)
            // Setting various property values
            const alink = document.createElement('a')
            alink.href = fileURL
            alink.download = `${playbook.title.replace(/\./g, '-')}.pdf`
            alink.click()
        }

        const onPdfDownloadSlides = async () => {
            pdfSlidesLoading.setTrue()

            const blob = await pdf(
                <PdfSlidesDocument onRender={pdfSlidesLoading.setFalse} playbook={playbook} />
            ).toBlob()

            downloadPdfFile(blob)
        }

        const onPdfDownloadA4 = async () => {
            pdfLoading.setTrue()

            const blob = await pdf(
                <PdfDocument
                    onRender={pdfLoading.setFalse}
                    favIcon={favIcon}
                    playbook={playbook}
                    brandKitHighlightColor={brandKitHighlightColor}
                />
            ).toBlob()

            downloadPdfFile(blob)
        }

        const { showErrorNotification } = useNotification()

        // gifUrl and video url can't be accessed directly
        // since it is possible that the page is stale
        const handleDownload = async (
            artifact: 'gif' | 'mp4',
            id: string,
            loading: UseBooleanType
        ) => {
            try {
                loading.setTrue()
                const res = await request(
                    `/c/v1/quickguidde/download?id=${id}&type=${artifact}`,
                    'GET'
                )

                const artifactRes = await fetch(res.url)
                const blob = await artifactRes.blob()

                const objectURL = URL.createObjectURL(blob)
                // create a tag with attributes
                let a = document.createElement('a')
                a.href = objectURL
                a.setAttribute('download', fileName.replace(/\./g, '-'))
                a.style.display = 'none'
                document.body.appendChild(a)
                a.click()
                // clean up
                document.body.removeChild(a)
                URL.revokeObjectURL(a.href)
                loading.setFalse()
            } catch (error) {
                loading.setFalse()
                showErrorNotification('Something went wrong')
                return null
            }
        }

        return (
            <Box width="100%">
                <SpacedGroup>
                    <WhiteButton
                        data-cy="share-dialog-download-mp4"
                        disabled={loadingMP4.isTrue || disableMP4}
                        startIcon={<Icon />}
                        style={{ zIndex: 4 }}
                        onClick={() => {
                            handleDownload('mp4', playbookId, loadingMP4)
                            logToAnalytics('download_playbook', {
                                playbookType: mode
                            })
                        }}
                    >
                        MP4
                    </WhiteButton>

                    <WhiteButton
                        data-cy="share-dialog-download-gif"
                        disabled={loadingGif.isTrue || disableGIF}
                        startIcon={<Icon />}
                        style={{ zIndex: 4 }}
                        onClick={() => {
                            handleDownload('gif', playbookId, loadingGif)
                            logToAnalytics('download_gif', {
                                playbookType: mode
                            })
                        }}
                    >
                        GIF
                    </WhiteButton>

                    {isQG(playbook) && (
                        <WhiteButton
                            data-cy="share-dialog-download-pdf"
                            disabled={pdfLoading.isTrue || brandKitLoading}
                            startIcon={<Icon />}
                            style={{ zIndex: 4 }}
                            onClick={onPdfDownloadA4}
                        >
                            Document (PDF)
                        </WhiteButton>
                    )}
                    {isQG(playbook) && (
                        <WhiteButton
                            data-cy="share-dialog-download-slides-pdf"
                            disabled={pdfSlidesLoading.isTrue}
                            startIcon={<Icon />}
                            style={{ zIndex: 4 }}
                            onClick={onPdfDownloadSlides}
                        >
                            Slides (PDF)
                        </WhiteButton>
                    )}
                </SpacedGroup>
            </Box>
        )
    }
)
